import { Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { shareReplay, map, tap } from 'rxjs/operators';

import { User } from './user';
import { UserModification } from './user-modification';
import { PinnedReport } from './pinned-report';
import { Account } from '../account/account';

import { ResponseWrapper, SKIP_AUTH_LOGOUT } from '@library/common/http';

import { PrefetchService, Preferences } from '@library/core/services';

import { Auth, CognitoUser } from '@aws-amplify/auth';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';

import * as FullStory from '@fullstory/browser';
import { IGenericType } from '@library/common/generictype';

@Injectable({
   providedIn: 'root',
})
export class UserService {
   url = 'user';

   working = false;

   _user?: User;

   CognitoUser?: CognitoUser;

   observable?: Observable<any>;

   constructor(
      private http: HttpClient,
      private prefetch: PrefetchService,
      private prefs: Preferences,
      private snackbar: MatSnackBar,
   ) { }

   get user(): Observable<User> {
      if ( this._user ) {
         return of( this._user );
      }

      if ( this.observable ) {
         return this.observable;
      }

      // newly logged in user, pull info from the db. This call is already retried by
      // handleSignedInState on transient auth failures, so opt out of the interceptor's
      // auto-logout-on-401/403 (see getUser) to let that retry run instead of being
      // preempted. Other callers needing user data (e.g. pinned_reports below) should
      // go through this same cached/protected pipeline rather than issuing their own
      // unprotected request to the same endpoint.
      return this.observable = this.getUser( true )
         .pipe(
            tap( user => {
               const us = user;
               if (us) {
                  FullStory.setUserVars({
                     displayName: us.fname + ' ' + us.lname,
                     email: us.username,
                  });
               }
               const havePrimary = us.accounts.some( a => a?.primary == true );
               const haveActive = us.accounts.some( a => a?.active == true );
               if( havePrimary == false && haveActive == false ) {
                  user.preferences.start = [];
                  user.preferences.start.push( { label: "USER > ACCOUNTS", path: "/user/account" });
               }
               this._user = user;
               localStorage.removeItem( 'user' );
            }),
            tap( user => this.prefs.prefs = user.preferences ),
            tap( _ => this.prefs.listen = true ),
            tap( _ => this.prefetch.prefetch() ),
            tap( _ => this.observable = undefined ),
            shareReplay()
         );
   }

   get accounts(): Observable<Account[]> {
      return this.user.pipe( map( user => user.accounts ), shareReplay() );
   }

   get pinned_reports(): Observable<PinnedReport[]> {
      // Reuse the cached, retried, SKIP_AUTH_LOGOUT-protected `user` pipeline instead
      // of issuing an independent, unprotected GET to the same endpoint. The previous
      // implementation (this.getUser() with no args) had no retry and did not opt out
      // of the interceptor's auto-logout-on-401/403, so a transient auth failure
      // hitting this call during the same post-login window handleSignedInState's
      // retry exists to survive (see the `user` getter above) could trigger an
      // immediate logout on its own — reproducing the "login forces relogin" symptom
      // via a different call site than the one already protected on `UserService.user`.
      return this.user.pipe( map( user => user.pinned_reports ), shareReplay() );
   }

   update( modify: UserModification ): Observable<any> {
      if ( !modify ) { return of( null ); }

      return this.http.patch( this.url, modify, { params: { useLambda: true } } ).pipe( tap( _ => this.logout() ));
   }

   logout() {
      this.reset();
   }

   destroy(): void {
      this.reset();
   }

   reset(): void {
      this._user = undefined;
      this.observable = undefined;
      localStorage.removeItem( 'user' );
      localStorage.removeItem('email');
      localStorage.removeItem('name');
   }

   getCognitoUser() {
      return Auth.currentUserPoolUser({ bypassCache: true }).then( CognitoUser => {
         return CognitoUser;
      }, error => {
         console.log (error);
      })
   }

   changePassword( currentUser: CognitoUser, oldPassword: string, newPassword: string ) {
      let authSnackbar: MatSnackBarRef<any>;

      this.working = true;

      return Auth.changePassword( currentUser, oldPassword, newPassword ).then( _ => {
         authSnackbar = this.snackbar.open( 'Your password has been successfully changed!', 'Close', { duration: 15000 } );
         authSnackbar.onAction().subscribe( _ => authSnackbar.dismiss() );
         this.working = false;
         return true;
      }, error => {
         let message = '';

         if( error.message ) {
            if( error.message == 'Incorrect username or password.' ) {
               message = 'Incorrect Old Password';
            }
            message = error.message;
         } else {
            message = 'Unknown Password change error please contact your administrator';
         }

         authSnackbar = this.snackbar.open( message, 'Close', { duration: 15000 } );
         authSnackbar.onAction().subscribe( _ => authSnackbar.dismiss() );
         this.working = false;
         return false;
      })
   }

   private getUser( skipAuthLogout = false ) {
      let params: IGenericType = { useLambda: true };

      // Only the initial post-login fetch (see the `user` getter) opts out of the
      // interceptor's auto-logout-on-401/403 — it already retries transient failures
      // itself. This is currently the only direct caller of getUser(); other consumers
      // of user data (e.g. pinned_reports) should go through the `user` getter above
      // instead of calling getUser() directly, so they share this same protection.
      const context = new HttpContext().set( SKIP_AUTH_LOGOUT, skipAuthLogout );

      return this.http.get<ResponseWrapper<User>>( this.url, { params, context } )
         .pipe(
            map( rw => rw.response.collection[0] ),
            map( user => {
               // throwError() returns an Observable, it does not raise. Passing
               // it on left `new User( <Observable> )` to be built from it, and
               // the missing-user case surfaced later as a TypeError on
               // user.accounts instead of an error the caller could handle.
               if ( !user ) { throw new Error( 'Missing User' ); }

               return new User( user );
            }),
         );
   }
}
